import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router';
import { fetchOpenFormContactDetails } from '../../../../common/fetch.js';
import { useDebounce } from '../../../../common/hooks/useDebounce.js';
import { selectMsg, t } from '../../../../common/i18n/index.js';
import { type Choices } from '../../OpenFormAnswers.js';
import { OpenFormGridCol, OpenFormGridRow } from '../../OpenFormComponents/OpenFormGrid.js';
import { OpenFormNotice } from '../../OpenFormComponents/OpenFormNotifications';
import { useAbortController } from '../../OpenFormHooks/useAbortController.js';
import { setValueOptions } from '../../OpenFormHooks/useOpenFormMethods.js';
import { useOpenFormAsync } from '../../OpenFormProvider.js';
import { concat, getValue } from '../../OpenFormUtils.js';
import type { ParameterizedSearchContactResult } from '../../../../generated/api/parameterizedSearchContactResult.js';
import type { OpenFormOutlet } from '../../OpenFormLayout/OpenFormLayoutOutlet';

// eslint-disable-next-line @typescript-eslint/naming-convention
const Combobox = ({ Account, Id, FirstName, LastName, Email, MobilePhone }: ParameterizedSearchContactResult) => {
  const label = concat(concat(FirstName, LastName).join(' '), Account?.Name).join(' | ');
  return {
    id: Id!,
    value: Id!,
    label: label,
    html: (
      <div>
        <div>{label}</div>
        <div>{concat(Email, MobilePhone).join(' | ')}</div>
      </div>
    ),
  };
};

const toMap = (...input: (ParameterizedSearchContactResult | undefined)[]) =>
  new Map(input.filter(Boolean).map(c => [c!.Id, c]));

const validateSearchTerm = (searchTerm: string) => searchTerm.length !== 1;

export const OpenFormQuestionContact = ({
  colWidth = 6,
  disabled,
  required,
  label,
  name,
  choices,
  onSelect,
  infoText,
}: {
  colWidth?: CL.GridColProps['colWidth'];
  disabled: boolean;
  required: boolean;
  label: string;
  name: string;
  choices: Choices | string | undefined;
  onSelect?: (c?: ParameterizedSearchContactResult) => void;
  infoText?: string;
}) => {
  const { account } = useOutletContext<OpenFormOutlet>() ?? {};
  const { register, setValue } = useFormContext();
  const async = useOpenFormAsync();
  const [_, setAbortController] = useAbortController();
  const [contacts, setContacts] = useState(toMap());
  const [showWarning, setShowWarning] = useState(false);
  const selectedValue = useMemo(() => getValue(choices), [choices]);
  const initialCheckDone = useRef(false);
  const fetchContacts = useCallback<(searchTerm?: string | null) => void>(
    searchTerm => {
      if (validateSearchTerm((searchTerm = (searchTerm ?? '').trim()))) {
        const { controller } = async.fetch
          .getContacts({ searchTerm })
          .resolved(({ searchRecords = [] } = {}) => setContacts(toMap(...searchRecords, contacts.get(selectedValue!))))
          .rejected(text => async.actions.notification({ text, type: 'warning' }))
          .cache(searchTerm)
          .execute({ disable: false, message: true });
        setAbortController(controller);
      }
    },
    [async, selectedValue, setAbortController, contacts]
  );

  useEffect(() => {
    fetchContacts();
    register(name, { disabled, required });
    setValue(name, selectedValue, setValueOptions);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialCheckDone.current || !selectedValue) {
      return;
    }
    initialCheckDone.current = true;

    fetchOpenFormContactDetails({ contactIds: selectedValue }).then(response => {
      const contactDetail = response.contactDetails?.find(c => c.contactId === selectedValue);
      if (contactDetail && !contactDetail.isActive) {
        setValue(name, undefined, setValueOptions);
      }
    });
  }, [selectedValue, name, setValue]);

  return (
    <OpenFormGridRow>
      <OpenFormGridCol colWidth={colWidth} className={classNames({ ['label--mandatory']: required })}>
        <CL.Combobox
          disabled={disabled}
          i18n_combobox_placeholderText={t.QRYV(selectMsg)}
          items={useMemo(() => Array.from(contacts.values()).map(Combobox), [contacts])}
          label={label}
          i18n_combobox_infoText={infoText}
          selectedItemFormat="label"
          selectedValue={selectedValue}
          onOptionsShow={() => setContacts(toMap(contacts.get(selectedValue!), ...contacts.values()))}
          onValueChange={useDebounce(fetchContacts, 500)}
          onValueSelect={value => {
            const contact = value ? contacts.get(value) : undefined;
            if (contact && account?.header?.accountName && contact.Account?.Name !== account?.header?.accountName) {
              setShowWarning(true);
            }
            onSelect ? onSelect(contact) : setValue(concat(name, 'contact').join('.'), contact);
            setValue(name, value, setValueOptions);
          }}
        />
      </OpenFormGridCol>
      {showWarning && (
        <OpenFormNotice
          text={t.I0G5('Are you sure you want to select a contact from another company?')}
          type="warning"
          hide={() => setShowWarning(false)}
        />
      )}
    </OpenFormGridRow>
  );
};
